// ─── Media Limits ────────────────────────────────────────
export const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_VIDEO_SIZE = 95 * 1024 * 1024; // 95 MB — proxied /upload path (CF Workers ~100MB body limit)
// Direct-to-R2 (presigned PUT) bypasses the Worker body limit, so large phone
// videos are allowed here. Bump this if guests need longer/4K clips.
export const MAX_VIDEO_SIZE_DIRECT = 1024 * 1024 * 1024; // 1 GB

export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
] as const;

export const ACCEPTED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/3gpp",
] as const;

export const ACCEPTED_MEDIA_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,
  ...ACCEPTED_VIDEO_TYPES,
] as const;

// ─── OTP ─────────────────────────────────────────────────
export const OTP_LENGTH = 6;
export const OTP_TTL_SECONDS = 600; // 10 minutes
export const OTP_MAX_ATTEMPTS = 5;
export const OTP_RATE_LIMIT = 3; // max requests per window
export const OTP_RATE_WINDOW_SECONDS = 600; // 10 minutes

// ─── Session ─────────────────────────────────────────────
export const SESSION_TTL_SECONDS = 86400; // 24 hours
export const SESSION_COOKIE_NAME = "sid";

// ─── Organizer Session ───────────────────────────────
export const ORGANIZER_SESSION_TTL_SECONDS = 86400; // 1 day
export const ORGANIZER_SESSION_COOKIE_NAME = "organizer_sid";

// ─── Admin Session ────────────────────────────────────
export const ADMIN_SESSION_TTL_SECONDS = 43200; // 12 hours
export const ADMIN_SESSION_COOKIE_NAME = "admin_sid";
export const ADMIN_OTP_TTL_SECONDS = 300; // 5 minutes (tighter window for admin)

// ─── Slideshow ───────────────────────────────────────────
export const WS_HEARTBEAT_INTERVAL_MS = 30_000;

// ─── R2 Key Builder ──────────────────────────────────────
export const buildR2Key = (slug: string, guestId: string, mediaId: string): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `weddings/${slug}/media/${y}/${m}/${d}/${guestId}/${mediaId}`;
};

// ─── Slug Constraints ────────────────────────────────────
export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 48;

// ─── Organizer Plans ─────────────────────────────────────
/**
 * FALLBACK plan limits — used when DB plans table is unavailable.
 * Source of truth is the `plans` table in D1.
 * @see migrations/007_dynamic_plans_audit.sql
 */
export type PlanId = "free" | "pro" | "max";

export interface PlanLimits {
  maxWeddings: number;
  maxGuestsPerWedding: number;
  maxStorageBytes: number;
  maxMediaPerWedding: number;
  maxReminderSends: number;
  /** F8: per-wedding SMS segment kotası — Twilio segment başına ücretlendiriyor. */
  maxSmsSegments: number;
}

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: {
    maxWeddings: 1,
    maxGuestsPerWedding: 20,
    maxStorageBytes: 100 * 1024 * 1024,       // 100 MB
    maxMediaPerWedding: 50,
    maxReminderSends: 1,
    maxSmsSegments: 50,
  },
  pro: {
    maxWeddings: 10,
    maxGuestsPerWedding: 200,
    maxStorageBytes: 10 * 1024 * 1024 * 1024,  // 10 GB
    maxMediaPerWedding: 500,
    maxReminderSends: 3,
    maxSmsSegments: 600,
  },
  max: {
    maxWeddings: 50,
    maxGuestsPerWedding: 1000,
    maxStorageBytes: 100 * 1024 * 1024 * 1024, // 100 GB
    maxMediaPerWedding: 5000,
    maxReminderSends: 5,
    maxSmsSegments: 3000,
  },
};

export const PLAN_LABELS: Record<PlanId, string> = {
  free: "Free",
  pro: "Pro",
  max: "Max",
};

// ─── SMS Campaigns ───────────────────────────────────────
export const SMS_TEMPLATE_PLACEHOLDERS = [
  "guest_name",
  "couple_names",
  "date",
  "venue",
  "rsvp_link",
  "opt_out_link",
  // Opsiyonel ikinci etkinlik — yalnız düğünde tanımlıysa anlamlı doluyor.
  "date2",
  "venue2",
  "event2_name",
] as const;
export type SmsTemplatePlaceholder = typeof SMS_TEMPLATE_PLACEHOLDERS[number];

export const SMS_TEMPLATE_MIN_LENGTH = 10;
export const SMS_TEMPLATE_MAX_LENGTH = 640;
export const SMS_MANUAL_SEGMENT_MAX_GUESTS = 500;
export const SMS_DISPATCH_CONCURRENCY = 5;
// Twilio TR outbound fiyatı (USD/segment) — UI maliyet tahmini için, fatura değil.
export const SMS_TR_PRICE_USD = 0.0305;

// ─── SMS Finansal / Güvenlik Korumaları ──────────────────
// Uygulama katmanı coğrafi allow-list (S3). Kampanya + preview yalnız bu
// prefix'lere gider; phoneSchema (OTP) global kalır. Twilio panel geo-permission
// ikinci savunma hattıdır.
export const SMS_ALLOWED_COUNTRY_PREFIXES = ["+90"] as const;
// Bir turun mutlak alıcı tavanı — plan düşürme (F6) ve `all` segment açığına karşı.
export const SMS_ROUND_RECIPIENT_HARD_CAP = 1000;
// Platform-geneli günlük segment tavanı (F5). Aşılırsa spend breaker tetiklenir.
// ~5000 segment ≈ $150/gün. Faz B ilk 48 saat düşük tutulup kademeli artırılır.
export const SMS_GLOBAL_DAILY_SEGMENT_CAP = 5000;
// Organizatör başına günlük preview (test) SMS tavanı (karar 2).
export const SMS_PREVIEW_DAILY_CAP = 5;
// Admin onay eşiği: round-1 alıcı sayısı bunu AŞAN kampanya, bir yönetici
// onaylayana kadar tek SMS göndermez. Segment/maliyet riski zaten
// smsSegmentDailyCap + spend breaker + F8 ile korunuyor; bu eşik "kaç kişiye
// tek seferde blast atılıyor" sosyal/itibar riskini kapsar.
// 300 neden: SMS_MANUAL_SEGMENT_MAX_GUESTS=500 ve SMS_ROUND_RECIPIENT_HARD_CAP=1000
// arasında — tipik düğün listesinin ÇOĞUNU otomatik geçirecek kadar yüksek,
// anormal büyük blast'ı yakalayacak kadar düşük.
export const SMS_APPROVAL_THRESHOLD = 300;
// Public opt-out link kök yolu (Alphanumeric sender tek-yönlü → STOP çalışmaz).
export const SMS_OPTOUT_PATH = "/api/o";

// Global günlük Verify (OTP) gönderim tavanı — FELAKET DEVRE KESİCİ (backstop).
// Pumping'e karşı asıl savunma Twilio panel geo-kilidi (+90) + Fraud Guard;
// bu, dağıtık numara-rotasyonlu pumping için son emniyet. EN BÜYÜK düğünün çok
// üstünde tutulur (max plan 1000 misafir + re-login) → normal düğünü throttle
// ETMEZ, yalnız saldırı anında tetiklenir. Aşılınca kritik alarm + 503.
export const VERIFY_GLOBAL_DAILY_CAP = 3000;

// Resend email günlük gönderim tavanı (yeni finansal kol). Fail-safe default.
export const EMAIL_GLOBAL_DAILY_CAP = 2000;

// QR tarama günlük yazma tavanı (F1) — visitor_hash zaten günlük rotate
// olduğu için bu, "aynı (ip,ua) kombinasyonu için günde en fazla N satır"
// anlamına gelir. Venue NAT'ı kırmaz (hash ip+ua+gün karışımı, IP DEĞİL).
export const QR_SCAN_VISITOR_DAILY_CAP = 10;

// ─── Join Self-Registration (public QR link) ──────────────────────────────
// Düğün başına 24 saatlik self-register tavanı. QR linki herkese açık: link
// sızarsa/bot Turnstile'ı geçerse misafir listesi ve SMS bütçesi burada durur.
// En büyük planın (1000 misafir) altında ama tek gecelik gerçek katılımın çok
// üstünde — normal düğünü throttle ETMEZ. Runtime'da financial-config ile
// ayarlanabilir (`selfRegisterDailyCap`).
export const SELF_REGISTER_DAILY_CAP = 300;

// Self-register'da kabul edilen numara prefix'leri. Twilio **Verify** panel
// izinleri +90'a kilitli (pumping savunması) → +49 bir misafir kod ALAMAZ,
// 502 görürdü. Doğrulanamayacak numarayı baştan reddedip anlamlı mesaj
// gösteriyoruz; yurtdışı misafiri organizatör elle ekler.
// ⚠️ Burayı genişletmek Twilio panelinde geo-permission açmadan İŞE YARAMAZ.
export const SELF_REGISTER_ALLOWED_PREFIXES = ["+90"] as const;

// Doğrulanmamış kayıt KV'de bu kadar saniye bekler (OTP kodunun ömrüyle uyumlu).
// Süre dolarsa misafir formu baştan doldurur — DB'ye hiçbir şey yazılmamıştır.
export const PENDING_REGISTRATION_TTL_SECONDS = 900; // 15 dk

// Soft-delete edilmiş medyanın R2 + D1'den kalıcı silinmeden önceki bekleme
// süresi. Aynı sayı üç yerde geçerli: temizlik cron'u (hard-delete + uyarı
// e-postası), "Silinenler" kurtarma listesinin penceresi ve UI'daki "N gün
// kaldı" metni — bu yüzden tek kaynak burada.
export const MEDIA_DELETE_GRACE_DAYS = 7;

/**
 * Medyayı kim sildi (migration 054 `media.deleted_by`). `guest` = misafirin
 * kendi kararı → durum KİLİTLİ (onay/ret/geri al yasak); admin salt okunur
 * görür, organizatör yalnız Kontrol Merkezi ayarı açıksa görür.
 */
export const MEDIA_DELETED_BY = ["guest", "organizer", "admin", "system"] as const;
export type MediaDeletedBy = (typeof MEDIA_DELETED_BY)[number];

// ─── Düğün ek paketleri (migration 056) ─────────────────────────────────────
/** "QR ile ekstra kayıt" — düğün paket limitinin üstünde QR'dan gelen ekstra kişi. */
export const WEDDING_ADDON_KINDS = ["qr_extra"] as const;
export type WeddingAddonKind = (typeof WEDDING_ADDON_KINDS)[number];
/** `pending` = açıldı ve AKTİF, ödeme bekliyor; `cancelled` = koltuklar düştü. */
export const WEDDING_ADDON_STATUSES = ["pending", "paid", "cancelled"] as const;
export type WeddingAddonStatus = (typeof WEDDING_ADDON_STATUSES)[number];
/** Kota bu orana gelince organizatöre bir kez erken uyarı. */
export const GUEST_CAPACITY_WARN_RATIO = 0.9;

// ─── Background Slot Names ─────────────────────────────────────────────────
export const BG_SLOTS = {
  GUEST_AUTH:      "bg_guest_auth",
  ORGANIZER_AUTH:  "bg_organizer_auth",
  GUEST_COUNTDOWN: "bg_guest_countdown",
  GUEST_RSVP:      "bg_guest_rsvp",
} as const;

export type BgSlot = typeof BG_SLOTS[keyof typeof BG_SLOTS];

/** R2 key for admin default background */
export function bgDefaultKey(slot: BgSlot): string {
  return `defaults/${slot}`;
}

/** R2 key for per-wedding organizer override */
export function bgWeddingKey(slug: string, slot: BgSlot): string {
  return `weddings/${slug}/${slot}`;
}

// ─── Join Theme System ────────────────────────────────────────────────────

export const JOIN_PRESETS = [
  { id: "romantic",     label: "Romantic",     dark: "#c9a96e", light: "#f3e8e0", primary: "#c9a96e" },
  { id: "modern",       label: "Modern",       dark: "#1e293b", light: "#f8fafc", primary: "#1e293b" },
  { id: "minimal",      label: "Minimal",      dark: "#1b1817", light: "#f7f7f7", primary: "#8d6b5e" },
  { id: "boho",         label: "Boho",         dark: "#7c3d26", light: "#fdf6f0", primary: "#c4704f" },
  { id: "dark-romance", label: "Dark Romance", dark: "#1a0a0e", light: "#f3e8e0", primary: "#c9a96e" },
] as const;

export const JOIN_PALETTES = [
  { id: "blush-gold",    label: "Blush & Gold",    colors: ["#c9a96e", "#f3e8e0", "#8d6b5e", "#1b1817", "#ffffff"] },
  { id: "modern-slate",  label: "Modern Slate",    colors: ["#1e293b", "#f8fafc", "#64748b", "#e2e8f0", "#ffffff"] },
  { id: "warm-ivory",    label: "Warm Ivory",      colors: ["#8d6b5e", "#f7f7f7", "#3d2c26", "#1b1817", "#ffffff"] },
  { id: "terracotta",    label: "Terracotta",      colors: ["#c4704f", "#fdf6f0", "#7c3d26", "#1a0e0a", "#ffffff"] },
  { id: "botanical",     label: "Botanical Sage",  colors: ["#4a7c59", "#f0f7f2", "#2d5a3d", "#1a2e22", "#ffffff"] },
  { id: "dark-wine",     label: "Dark Wine",       colors: ["#6b1a2a", "#1a0a0e", "#c9a96e", "#f3e8e0", "#ffffff"] },
] as const;

export const JOIN_FONT_PAIRS = [
  { id: "elegant",   label: "Elegant",   heading: "Playfair Display",   body: "Inter",          googleFonts: "Playfair+Display:ital,wght@0,700;1,400&family=Inter:wght@400;500" },
  { id: "modern",    label: "Modern",    heading: "DM Sans",            body: "DM Sans",        googleFonts: "DM+Sans:wght@400;500;700" },
  { id: "romantic",  label: "Romantic",  heading: "Cormorant Garamond", body: "Lato",           googleFonts: "Cormorant+Garamond:ital,wght@0,700;1,400&family=Lato:wght@400;700" },
  { id: "editorial", label: "Editorial", heading: "Libre Baskerville",  body: "Source Sans 3",  googleFonts: "Libre+Baskerville:ital,wght@0,700;1,400&family=Source+Sans+3:wght@400;600" },
  { id: "minimal",   label: "Minimal",   heading: "Inter",              body: "Inter",          googleFonts: "Inter:wght@400;500;700" },
  { id: "classic",   label: "Classic",   heading: "Merriweather",       body: "Open Sans",      googleFonts: "Merriweather:ital,wght@0,700;1,400&family=Open+Sans:wght@400;600" },
  { id: "bold",      label: "Bold",      heading: "Montserrat",         body: "Montserrat",     googleFonts: "Montserrat:wght@400;700;900" },
  { id: "soft",      label: "Soft",      heading: "Nunito",             body: "Nunito",         googleFonts: "Nunito:wght@400;600;700" },
] as const;

export type JoinPaletteId = typeof JOIN_PALETTES[number]["id"];
export type JoinFontPairId = typeof JOIN_FONT_PAIRS[number]["id"];

/** Resolve palette by ID, falls back to warm-ivory */
export function resolveJoinPalette(id?: string) {
  return JOIN_PALETTES.find(p => p.id === id) ?? JOIN_PALETTES[2];
}

/** Resolve font pair by ID, falls back to elegant */
export function resolveJoinFontPair(id?: string) {
  return JOIN_FONT_PAIRS.find(f => f.id === id) ?? JOIN_FONT_PAIRS[0];
}

/** Resolve preset by ID, falls back to minimal */
export function resolveJoinPreset(id?: string) {
  return JOIN_PRESETS.find(p => p.id === id) ?? JOIN_PRESETS[2];
}
